import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import Cookies from 'js-cookie';
import { MessageDetailSkeleton } from '../components/Skeletons';
import '../styles/messageDetail.css';
import { UserContext } from '../contexts/UserContext';

const MessageDetail = () => {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const { redirectToLogin } = useContext(UserContext);
  const [messageDetail, setMessageDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'recipients'

  // Fetch message details from API
  const fetchMessageDetail = useCallback(async (isRefresh = false) => {
    try {
      if (!isRefresh) {
        setLoading(true);
      }
      setError(null);

      const token = Cookies.get('authToken');
      if (!token) {
        redirectToLogin(window.location.pathname);
        return;
      }

      const response = await axios.get(
        `${config.backendUrl}/messages/${messageId}/`,
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setMessageDetail(response.data.message);
    } catch (error) {
      console.error('Error fetching message detail:', error);
      if (error.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
        setTimeout(() => redirectToLogin('/login'), 2000);
      } else if (error.response?.status === 404) {
        setError('Message introuvable.');
      } else {
        setError(error.response?.data?.error || 'Erreur lors du chargement du message');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [messageId, navigate]);

  useEffect(() => {
    fetchMessageDetail();
  }, [fetchMessageDetail]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    fetchMessageDetail(true);
  }, [fetchMessageDetail]);

  const handleBack = useCallback(() => {
    navigate('/sms-history');
  }, [navigate]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  }, []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'PENDING':
        return '#FFA500'; // Orange
      case 'SENDING':
      case 'IN_PROGRESS':
        return '#003366'; // Brand blue
      case 'COMPLETED':
        return '#28A745'; // Green
      case 'FAILED':
        return '#c51618'; // Brand red
      case 'CANCELLED':
        return '#6C757D'; // Gray
      default:
        return '#6C757D';
    }
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'PENDING':
        return 'fa-clock';
      case 'SENDING':
      case 'IN_PROGRESS':
        return 'fa-paper-plane';
      case 'COMPLETED':
        return 'fa-check-circle';
      case 'FAILED':
        return 'fa-times-circle';
      case 'CANCELLED':
        return 'fa-stop-circle';
      default:
        return 'fa-question-circle';
    }
  }, []);

  const getRecipientStatusColor = useCallback((status) => {
    switch (status) {
      case 'PENDING':
        return '#FFA500'; // Orange
      case 'SENT':
        return '#003366'; // Brand blue
      case 'DELIVERED':
        return '#28A745'; // Green
      case 'FAILED':
      case 'BOUNCED':
        return '#c51618'; // Brand red
      default:
        return '#6C757D';
    }
  }, []);

  if (loading && !refreshing) {
    return (
      <div className="message-detail-container">
        <MessageDetailSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="message-detail-container">
        <div className="message-detail-error-container">
          <i className="fas fa-exclamation-triangle error-icon"></i>
          <h3>Erreur</h3>
          <p>{error}</p>
          <button className="btn-retry" onClick={handleRefresh}>
            Réessayer
          </button>
          <button className="btn-back" onClick={handleBack}>
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  if (!messageDetail) {
    return null;
  }

  const statusColor = getStatusColor(messageDetail.status);
  const statusIcon = getStatusIcon(messageDetail.status);
  const deliveryRate = messageDetail.recipient_count > 0 
    ? Math.round((messageDetail.delivered_count / messageDetail.recipient_count) * 100) 
    : 0;

  return (
    <div className="message-detail-container">
      {/* Header */}
      <div className="detail-header">
        <button className="btn-back-header" onClick={handleBack}>
          <i className="fas fa-arrow-left"></i>
          <span>Retour</span>
        </button>
        <h1>Détails du Message</h1>
        <button 
          className={`btn-refresh-header ${refreshing ? 'spinning' : ''}`}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <i className="fas fa-sync-alt"></i>
        </button>
      </div>

      {/* Status Card */}
      <div className="status-card">
        <div className="status-badge" style={{ backgroundColor: statusColor }}>
          <i className={`fas ${statusIcon}`}></i>
          <span>{messageDetail.status_display}</span>
        </div>
        <div className="status-info">
          <span className="status-label">Statut du message</span>
        </div>
      </div>

      {/* Message Content */}
      <div className="message-content-card">
        <h2>Message</h2>
        <div className="message-text-display">
          <p>{messageDetail.message}</p>
        </div>
        <div className="message-meta">
          <div className="meta-item">
            <i className="fas fa-calendar-alt"></i>
            <span>Créé le {formatDate(messageDetail.created_at)}</span>
          </div>
          {messageDetail.started_at && (
            <div className="meta-item">
              <i className="fas fa-play-circle"></i>
              <span>Commencé le {formatDate(messageDetail.started_at)}</span>
            </div>
          )}
          {messageDetail.completed_at && (
            <div className="meta-item">
              <i className="fas fa-check-circle"></i>
              <span>Terminé le {formatDate(messageDetail.completed_at)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className="stats-section">
        <h2>Statistiques</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#007BFF20', color: '#007BFF' }}>
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-content">
              <span className="stat-value">{messageDetail.recipient_count}</span>
              <span className="stat-label">Destinataires</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#FFA50020', color: '#FFA500' }}>
              <i className="fas fa-paper-plane"></i>
            </div>
            <div className="stat-content">
              <span className="stat-value">{messageDetail.sent_count}</span>
              <span className="stat-label">Envoyés</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#28A74520', color: '#28A745' }}>
              <i className="fas fa-check-double"></i>
            </div>
            <div className="stat-content">
              <span className="stat-value">{messageDetail.delivered_count}</span>
              <span className="stat-label">Livrés</span>
            </div>
          </div>
          
          {messageDetail.failed_count > 0 && (
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#DC354520', color: '#DC3545' }}>
                <i className="fas fa-times-circle"></i>
              </div>
              <div className="stat-content">
                <span className="stat-value">{messageDetail.failed_count}</span>
                <span className="stat-label">Échecs</span>
              </div>
            </div>
          )}
          
          {messageDetail.pending_count > 0 && (
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#6C757D20', color: '#6C757D' }}>
                <i className="fas fa-clock"></i>
              </div>
              <div className="stat-content">
                <span className="stat-value">{messageDetail.pending_count}</span>
                <span className="stat-label">En attente</span>
              </div>
            </div>
          )}
        </div>

        {/* Delivery Rate */}
        {messageDetail.status === 'COMPLETED' && (
          <div className="delivery-rate-section">
            <h3>Taux de livraison</h3>
            <div className="delivery-rate-bar-large">
              <div 
                className="delivery-rate-fill-large" 
                style={{ width: `${deliveryRate}%`, backgroundColor: statusColor }}
              >
                <span className="delivery-rate-percentage">{deliveryRate}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Cost Information */}
        <div className="cost-section">
          <div className="cost-item">
            <span className="cost-label">Coût par SMS</span>
            <span className="cost-value">{messageDetail.cost_per_sms} Zaires</span>
          </div>
          <div className="cost-item total">
            <span className="cost-label">Coût total</span>
            <span className="cost-value">{messageDetail.total_cost} Zaires</span>
          </div>
        </div>
      </div>

      {/* Recipients Section */}
      {messageDetail.recipients && messageDetail.recipients.length > 0 && (
        <div className="recipients-section">
          <h2>Destinataires ({messageDetail.recipients.length})</h2>
          <div className="recipients-list">
            {messageDetail.recipients.map((recipient) => (
              <div key={recipient.id} className="recipient-card">
                <div className="recipient-info">
                  <div className="recipient-avatar">
                    <i className="fas fa-user"></i>
                  </div>
                  <div className="recipient-details">
                    <h4>{recipient.recipient_name}</h4>
                    <p>@{recipient.recipient_username}</p>
                    <p className="recipient-phone">{recipient.phone_number}</p>
                  </div>
                </div>
                <div className="recipient-status-info">
                  <div 
                    className="recipient-status-badge" 
                    style={{ backgroundColor: getRecipientStatusColor(recipient.delivery_status) }}
                  >
                    {recipient.status_display}
                  </div>
                  {recipient.sent_at && (
                    <span className="recipient-timestamp">
                      <i className="fas fa-clock"></i> {formatDate(recipient.sent_at)}
                    </span>
                  )}
                  {recipient.error_message && (
                    <span className="recipient-error">
                      <i className="fas fa-exclamation-circle"></i> {recipient.error_message}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageDetail;
